import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, user, isRestoring } = useAuthStore()
  const location = useLocation()
  const startupLoggedRef = React.useRef(false)
  const [timedOut, setTimedOut] = React.useState(false)

  // 检查是否有本地缓存（之前登录过的用户）
  // 有缓存时乐观放行，不等 restoreSession 完成；无缓存时保持阻塞等待
  const hasLocalCache = (() => {
    try {
      return !!localStorage.getItem('modern_notes_user_id')
    } catch {
      return false
    }
  })()

  // [Startup] 埋点：首次放行（乐观放行或 isRestoring=false）
  React.useEffect(() => {
    if (!startupLoggedRef.current) {
      // 乐观放行（有缓存 + isRestoring）或正常放行（!isRestoring）
      if ((hasLocalCache && isRestoring) || !isRestoring) {
        startupLoggedRef.current = true
        console.log('[Startup] ProtectedRoute 放行:', Math.round(performance.now()), 'ms',
          hasLocalCache && isRestoring ? '(乐观放行)' : '')
      }
    }
  }, [isRestoring, hasLocalCache])

  // 15s 超时：restoreSession 挂起时避免永久 spinner，显示重试按钮
  // 仅在无本地缓存（阻塞等待）时启用
  React.useEffect(() => {
    if (!isRestoring || hasLocalCache) {
      setTimedOut(false)
      return
    }
    const timer = setTimeout(() => setTimedOut(true), 15000)
    return () => clearTimeout(timer)
  }, [isRestoring, hasLocalCache])

  // 无本地缓存 + 正在恢复会话 → 显示 spinner（等待恢复）
  if (isRestoring && !hasLocalCache) {
    if (timedOut) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-900">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">恢复登录状态超时</p>
            <button
              onClick={() => {
                setTimedOut(false)
                useAuthStore.getState().setRestoring(true)
                useAuthStore.getState().restoreSession()
              }}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              type="button"
            >
              重试
            </button>
          </div>
        </div>
      )
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">正在恢复登录状态...</p>
        </div>
      </div>
    )
  }

  // 如果 URL 中有 OAuth 回调参数或回调正在处理中，不重定向到登录页
  // useOAuthUrlCallback 正在处理回调，重定向到 /login 会触发 LoginPage 自动登录，
  // 导致第二次 oauth_login 中断正在进行的回调处理
  const params = new URLSearchParams(window.location.search)
  const hasOAuthCallback = params.has('code') && params.has('state')
  const isCallbackProcessing = sessionStorage.getItem('oauth_callback_processing') === 'true'

  // restoreSession 已完成但未认证（token 过期且刷新失败）→ 跳转登录
  // 有本地缓存 + isRestoring（乐观放行中）→ 不跳转，继续渲染
  if (!isAuthenticated || !user) {
    if (hasOAuthCallback || isCallbackProcessing) {
      // 回调正在处理中，渲染子组件让 useOAuthUrlCallback（App 层）完成处理
      return <>{children}</>
    }
    // 乐观放行中（有缓存 + isRestoring）→ 继续渲染，等 restoreSession 完成
    if (hasLocalCache && isRestoring) {
      return <>{children}</>
    }
    // 保存当前路径，登录后可以跳转回来
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // ★ 华为登录未设置跨端凭证（首次登录强制引导）→ 绑定页（不可跳过）
  if (user.needsEmailBinding) {
    return <Navigate to="/bind-email" replace />
  }

  return <>{children}</>
}
